/**
 * Utility class for handling version tracking and changelog display logic
 */
export class VersionUtil {
    private static readonly STORAGE_KEY = 'changelog_dismissed_version';
    private static readonly LAST_SEEN_VERSION_KEY = 'last_seen_version';

    /**
     * Check if the changelog should be shown for the current version
     * @param currentVersion Current app version
     * @param forceShow Force show changelog regardless of settings
     * @returns true if changelog should be shown
     */
    public static shouldShowChangelog(currentVersion: string, forceShow: boolean = false): boolean {
        // If explicitly forced to show (e.g., user clicked "What's New" button)
        if (forceShow) {
            return true;
        }

        const dismissedVersion = localStorage.getItem(this.STORAGE_KEY);
        const lastSeenVersion = localStorage.getItem(this.LAST_SEEN_VERSION_KEY);

        // If user explicitly dismissed this version with "Don't show again", don't show
        if (dismissedVersion === currentVersion) {
            return false;
        }

        // Show automatically on any version change or first visit
        if (lastSeenVersion === null) {
            // First time user - show changelog
            return true;
        }

        if (lastSeenVersion !== currentVersion) {
            // Show on any version change (major, minor, or patch)
            return true;
        }

        // For same version, always show unless explicitly dismissed
        // This ensures overlay redisplays on reload until user checks "Don't show again"
        return true;
    }

    /**
     * Mark the current version as seen
     * @param currentVersion Current app version
     */
    public static markVersionSeen(currentVersion: string): void {
        localStorage.setItem(this.LAST_SEEN_VERSION_KEY, currentVersion);
    }

    /**
     * Dismiss changelog for the current version
     * @param currentVersion Current app version
     */
    public static dismissChangelogForVersion(currentVersion: string): void {
        localStorage.setItem(this.STORAGE_KEY, currentVersion);
    }

    /**
     * Get the last seen version
     * @returns Last seen version or null if none
     */
    public static getLastSeenVersion(): string | null {
        return localStorage.getItem(this.LAST_SEEN_VERSION_KEY);
    }

    /**
     * Get the dismissed version
     * @returns Dismissed version or null if none
     */
    public static getDismissedVersion(): string | null {
        return localStorage.getItem(this.STORAGE_KEY);
    }

    /**
     * Clear all version tracking data (useful for testing)
     */
    public static clearVersionData(): void {
        localStorage.removeItem(this.STORAGE_KEY);
        localStorage.removeItem(this.LAST_SEEN_VERSION_KEY);
    }

    /**
     * Compare two semantic versions
     * @param version1 First version
     * @param version2 Second version
     * @returns -1 if version1 < version2, 0 if equal, 1 if version1 > version2
     */
    public static compareVersions(version1: string, version2: string): number {
        const v1Parts = version1.split('.').map(n => parseInt(n, 10));
        const v2Parts = version2.split('.').map(n => parseInt(n, 10));

        for (let i = 0; i < Math.max(v1Parts.length, v2Parts.length); i++) {
            const v1Part = v1Parts[i] || 0;
            const v2Part = v2Parts[i] || 0;

            if (v1Part < v2Part) return -1;
            if (v1Part > v2Part) return 1;
        }

        return 0;
    }

    /**
     * Check if a version is newer than another
     * @param newVersion Version to check
     * @param oldVersion Version to compare against
     * @returns true if newVersion is newer
     */
    public static isNewerVersion(newVersion: string, oldVersion: string): boolean {
        return this.compareVersions(newVersion, oldVersion) > 0;
    }
}